import React, { useMemo, useState } from "react";
import {
  formatCurrencyNetto,
  formatDateSwiss,
  parseLocalDate,
  resolvePhaseName,
} from "./zahlungsplanUtils";
import { buildBelegFileUrl } from "../../services/azureBelegeService";

// Strukturierte Übersicht aller Belege eines Projekts (Button "Belege" im
// Zahlungsplan): pro Beleg Gewerk (Partner) und Phase der zugeordneten
// Positionen, Bezahlt-Status und Total; Klick auf eine Zeile öffnet die
// PDF-Vorschau.
const ZahlungsplanBelegeListe = ({
  belege,
  entries,
  phases = [],
  readOnly,
  onDelete,
  onClose,
}) => {
  const [previewBeleg, setPreviewBeleg] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const rows = useMemo(() => {
    return belege.map((beleg) => {
      const linked = entries.filter((e) => e.beleg_id === beleg.id);
      const gewerke = [...new Set(linked.map((e) => e.partner_name || "Sonstige"))];
      const phasen = [
        ...new Set(
          linked.map((e) => resolvePhaseName(e.phase, phases) || "ohne Phase")
        ),
      ];
      const total = linked.reduce(
        (sum, e) => sum + (e.netto_betrag || e.betrag || 0),
        0
      );
      const paidCount = linked.filter((e) => e.status === "paid").length;
      let bezahlt = "–";
      let bezahltClass = "none";
      if (linked.length > 0) {
        if (paidCount === linked.length) {
          bezahlt = "Bezahlt";
          bezahltClass = "paid";
        } else if (paidCount > 0) {
          bezahlt = "Teilweise bezahlt";
          bezahltClass = "partial";
        } else {
          bezahlt = "Offen";
          bezahltClass = "open";
        }
      }
      return { beleg, linked, gewerke, phasen, total, bezahlt, bezahltClass };
    });
  }, [belege, entries, phases]);

  const totalSum = useMemo(
    () => rows.reduce((sum, r) => sum + r.total, 0),
    [rows]
  );

  const handleDelete = async (row) => {
    if (readOnly || deletingId) return;
    const positionen = row.linked.length;
    const message =
      positionen > 0
        ? `Beleg "${row.beleg.fileName}" wirklich löschen? Die Zuordnung zu ${positionen} Position${positionen === 1 ? "" : "en"} wird entfernt (die Zahlungen selbst bleiben bestehen).`
        : `Beleg "${row.beleg.fileName}" wirklich löschen?`;
    if (!window.confirm(message)) return;
    setDeletingId(row.beleg.id);
    try {
      await onDelete(row.beleg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="zp-belege-overlay" onClick={onClose}>
      <div className="zp-belege-panel" onClick={(e) => e.stopPropagation()}>
        <div className="zp-belege-header">
          <h3>Belege</h3>
          <button className="btn btn-secondary" onClick={onClose}>
            Schliessen
          </button>
        </div>

        <div className="zp-belege-body">
          {rows.length === 0 ? (
            <div className="zp-empty-state">
              Noch keine Belege vorhanden. Belege werden beim Setzen einer
              Zahlung auf «Erhalten» hochgeladen.
            </div>
          ) : (
            <table className="zp-belege-table">
              <thead>
                <tr>
                  <th>Beleg</th>
                  <th>Hochgeladen</th>
                  <th>Gewerk</th>
                  <th>Phase</th>
                  <th className="num">Positionen</th>
                  <th className="num">Total (netto)</th>
                  <th>Bezahlt</th>
                  {!readOnly && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const date = parseLocalDate(row.beleg.hochgeladenAm);
                  return (
                    <tr
                      key={row.beleg.id}
                      className="zp-belege-row"
                      onClick={() => setPreviewBeleg(row.beleg)}
                      title="Klicken für Vorschau"
                    >
                      <td className="zp-belege-name">{row.beleg.fileName}</td>
                      <td>{date ? formatDateSwiss(date) : "–"}</td>
                      <td>{row.gewerke.length ? row.gewerke.join(", ") : "–"}</td>
                      <td>{row.phasen.length ? row.phasen.join(", ") : "–"}</td>
                      <td className="num">{row.linked.length || "–"}</td>
                      <td className="num">
                        {row.total ? formatCurrencyNetto(row.total) : "–"}
                      </td>
                      <td>
                        <span className={`zp-belege-status ${row.bezahltClass}`}>
                          {row.bezahlt}
                        </span>
                      </td>
                      {!readOnly && (
                        <td className="zp-belege-actions">
                          <button
                            className="zp-belege-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(row);
                            }}
                            disabled={deletingId === row.beleg.id}
                            title="Beleg löschen"
                          >
                            {deletingId === row.beleg.id ? "…" : "Löschen"}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>
                    Total ({rows.length} Beleg{rows.length === 1 ? "" : "e"})
                  </td>
                  <td className="num">{formatCurrencyNetto(totalSum)}</td>
                  <td colSpan={readOnly ? 1 : 2} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {previewBeleg && (
          <div
            className="zp-beleg-preview-overlay"
            onClick={() => setPreviewBeleg(null)}
          >
            <div
              className="zp-beleg-preview"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="zp-beleg-preview-header">
                <h4>{previewBeleg.fileName}</h4>
                <div className="zp-beleg-preview-actions">
                  <a
                    href={buildBelegFileUrl(previewBeleg.blobPath)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary"
                  >
                    In neuem Tab öffnen
                  </a>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setPreviewBeleg(null)}
                  >
                    Schliessen
                  </button>
                </div>
              </div>
              <iframe
                className="zp-beleg-preview-frame"
                src={buildBelegFileUrl(previewBeleg.blobPath)}
                title={previewBeleg.fileName}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZahlungsplanBelegeListe;
